import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

interface Requirement {
  id: string;
  label: string;
  test: (value: string) => boolean;
}

const REQUIREMENTS: Requirement[] = [
  { id: "min-length", label: "At least 8 characters", test: (v) => v.length >= 8 },
  { id: "uppercase", label: "One uppercase letter (A–Z)", test: (v) => /[A-Z]/.test(v) },
  { id: "lowercase", label: "One lowercase letter (a–z)", test: (v) => /[a-z]/.test(v) },
  { id: "number", label: "One number (0–9)", test: (v) => /\d/.test(v) },
];

/**
 * Purely presentational live password checklist for the sign-up page.
 *
 * It renders alongside Clerk's prebuilt <SignUp /> widget and observes the
 * password <input> that Clerk renders, updating as the user types. It never
 * intercepts, blocks, or alters submission — Clerk remains the sole authority
 * on password validation and account creation.
 */
export function SignUpPasswordChecklist({
  container,
}: {
  container: React.RefObject<HTMLDivElement | null>;
}) {
  const [password, setPassword] = useState("");
  const [fieldPresent, setFieldPresent] = useState(false);
  const trackedInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const root = container.current;
    if (!root) return;

    const findPasswordInput = () =>
      root.querySelector<HTMLInputElement>('input[name="password"][type="password"]') ??
      root.querySelector<HTMLInputElement>('input[name="password"]');

    const sync = () => {
      const input = findPasswordInput();
      trackedInputRef.current = input;
      setFieldPresent(!!input);
      setPassword(input?.value ?? "");
    };

    // Clerk renders its form asynchronously and swaps steps (e.g. after the
    // email-verification step), so watch the subtree for the field appearing
    // or disappearing rather than assuming it exists on mount.
    const observer = new MutationObserver(sync);
    observer.observe(root, { childList: true, subtree: true });

    // Native listener on the wrapper: "input" bubbles, so this catches typing
    // in Clerk's field without touching the widget itself.
    const onInput = (event: Event) => {
      if (event.target === trackedInputRef.current && trackedInputRef.current) {
        setPassword(trackedInputRef.current.value);
      }
    };
    root.addEventListener("input", onInput);

    sync();

    return () => {
      observer.disconnect();
      root.removeEventListener("input", onInput);
    };
  }, [container]);

  if (!fieldPresent) return null;

  return (
    <div
      data-testid="password-checklist"
      className="mt-4 w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-sm"
    >
      <p className="mb-2 text-xs font-medium text-muted-foreground">
        Your password must include:
      </p>
      <ul className="space-y-1.5">
        {REQUIREMENTS.map((req) => {
          const met = req.test(password);
          return (
            <li
              key={req.id}
              data-testid={`password-req-${req.id}`}
              data-met={met}
              className={`flex items-center gap-2 text-sm transition-colors ${
                met ? "text-emerald-600 dark:text-emerald-500" : "text-muted-foreground"
              }`}
            >
              {met ? (
                <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <X className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
              )}
              <span>{req.label}</span>
              <span className="sr-only">{met ? "(met)" : "(not met)"}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
